
import { Component, Vue, Inject, Prop } from 'vue-property-decorator'
import { InjectionKey } from '../../injection-keys'
import { TenantConfig } from '../../../tenants/types'
import ImageSelector from '../Generics/Upload/ImageSelector.vue'
import ImagesPreview from '../Generics/Images/ImagesPreview.vue'
import NoteList from './NoteList.vue'
import { Note, NoteImage } from '../../../shared/types/notes/notes'
import { getNowNoFormat } from '../../utils/dates'
import { dateTypes } from '../../../shared/types/dates/date-models'
import { ColorsList } from '../../utils'
import { convertImagesToBase64 } from '../../../shared/helpers/images'
import { showConfirmDialog, showErrorSnackbar, showSuccessSnackbar} from '../../../shared/helpers/alerts-popups'
@Component({
  components: {
    ImageSelector,
    ImagesPreview,
    NoteList
  }
})
export default class NoteForm extends Vue {
  @Prop({ default: '' }) value!: string
  @Prop({ default: 'id' }) idKey!: string
  @Prop({ default: () => [] }) images!: (File | string)[]
  @Prop({ default: false }) isEditing!: boolean;
  
  @Prop({ required: true }) fetchNotes!: () => Promise<Note[]>;
  @Prop({ required: true }) addNote!: (note: Note) => Promise<any>
  @Prop({ required: false }) updateNote?: (note: Note) => Promise<any>
  @Prop({ required: false }) deleteNoteApi?: (note: Note) => Promise<any>;
  @Prop({ type: Boolean, default: false }) showSamplingLabels!: boolean;
  @Prop({ default: null }) selectedNoteId!: number | null;
  @Inject({ from: InjectionKey.Tenant, default: null }) readonly tenant!: TenantConfig | null;
  removedImageIds: number[] = []
  orangeColor: string = ColorsList.mid_orange;
  loadingNotes: boolean = true;
  savingNote: boolean = false;
  deletingNote: boolean = false;

  localText: string = ''
  localImages: (File | string)[] = []
  existingNotes: Note[] = [];
  editingNote: Note | null = null;

  

  isPersistedNote(note: Note): boolean {
    return note[this.idKey] !== undefined
  }

  async loadNotes() {
    this.loadingNotes = true;
    try {
      const notes = await this.fetchNotes();
      this.existingNotes = notes.map(n => ({
        ...n,
        images: n.images || []
      }));
    } catch (err) {
      showErrorSnackbar('Error al obtener las notas')
    } finally {
      this.loadingNotes = false;
    }
  }

  async mounted() {
    this.localText = this.value
    this.localImages = [...this.images];
    await this.loadNotes();
  }

 async emitSave() {
    const now = getNowNoFormat().format(dateTypes.globalFilterDateFormat);
    const isEditing = !!this.editingNote;

    this.savingNote = true;

    try {
      const newImages = (this.localImages || []).filter(
        (i: any) => !(i && typeof i === 'object' && 'id' in i)
      );

      const base64NewImages = await convertImagesToBase64(newImages);

      const baseNote: any = {
        ...(this.editingNote || {}),
        text: this.localText.trim(),
        images: base64NewImages,
        // Solo backends con muestreo (`features.samplingReport`) conocen `is_sampling`
        ...(this.tenant && this.tenant.features.samplingReport ? { is_sampling: false } : {}),
      };

      if (isEditing) {
        baseNote.remove_images = this.removedImageIds;
        if (this.updateNote) {
          await this.updateNote(baseNote);
          showSuccessSnackbar('Nota modificada')
          const idx = this.existingNotes.findIndex(n => n[this.idKey] === baseNote[this.idKey]);
          if (idx !== -1) this.$set(this.existingNotes, idx, baseNote);
          await this.loadNotes();
        }
      } else {
        await this.addNote(baseNote);
        showSuccessSnackbar('Nota agregada')
        this.existingNotes.unshift(baseNote);
      }

      await this.loadNotes();
      this.reset();
    } catch (err) {
      showErrorSnackbar('No se pudo modificar la nota');
      this.reset();
    } finally {
      this.savingNote = false;
    }
  }

  editNote(note: Note) {
    this.editingNote = note
    this.localText = note.text || ''
    this.localImages = [...(note.images || [])] as any[];
    this.removedImageIds = []
  }

  cancelEdit() {
    this.editingNote = null
    this.localText = ''
    this.localImages = []
    this.removedImageIds = []
  }


  async deleteNote(note: Note) {
    const ok = await showConfirmDialog('Eliminar nota', '¿Deseás eliminar esta nota?');
    if (!ok) return;

    if (this.deleteNoteApi) {
      this.deletingNote = true;
      try {
        await this.deleteNoteApi(note);
        this.existingNotes = this.existingNotes.filter(n => n !== note);
        showSuccessSnackbar('Nota eliminada con éxito.');
      } catch (err) {
        showErrorSnackbar('No se pudo eliminar la nota')
        console.error('Error al eliminar nota:', err);
      }finally {
        await this.loadNotes();
        this.deletingNote = false;
      }
    }
  }

  removeImage(index: number, image) {
    if (image && typeof image === 'object' && 'id' in image && typeof image.id === 'number') {
      this.removedImageIds.push(image.id);
    }

    this.localImages.splice(index, 1);
  }

  reset() {
    this.localText = ''
    this.localImages = []
    this.editingNote = null;
    this.removedImageIds = []
  }
}
